import React, { useMemo, useState, useEffect } from 'react'
import { apiGet, apiPost, apiPut } from '../utils/api'

interface Player { id: string; name: string }
interface Tournament { id: string; name: string; memberships?: Array<{ playerId: string; level?: string }> }
type CorrectionMode = 'DISCOUNT' | 'SURCHARGE'
type DiscountReason = 'LOYALTY'|'MARKETING'|'ORGANIZER'|'SPONSOR'|'PRIZE'|'LOTTERY'
type SurchargeReason =
  | 'PENALTY'
  | 'LATE_PAYMENT'
  | 'DISCIPLINE'
  | 'MATCH_RESCHEDULE_EXTRA_COURT_RESERVE'
  | 'FIRST_MATCH_NO_SHOW_COURT_RESERVE'
  | 'OTHER'

export default function PlayerDiscountModal({
  tournament,
  players,
  isOpen,
  onClose,
  onSuccess,
  prefillPlayerId,
  editingId,
}: {
  tournament: Tournament
  players: Player[]
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  prefillPlayerId?: string
  editingId?: string
}) {
  const participantIds = useMemo(() => new Set((tournament.memberships || []).map(m => m.playerId)), [tournament])
  const [playerId, setPlayerId] = useState('')
  const [amount, setAmount] = useState('')
  const [correctionMode, setCorrectionMode] = useState<CorrectionMode>('DISCOUNT')
  const [reason, setReason] = useState<DiscountReason | SurchargeReason>('LOYALTY')
  const [comment, setComment] = useState('')
  const [docDate, setDocDate] = useState('')
  const [level, setLevel] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isOpen) {
      if (editingId) {
        loadDiscountData(editingId)
      } else {
        setPlayerId(prefillPlayerId || '')
        setAmount('')
        setCorrectionMode('DISCOUNT')
        setReason('LOYALTY')
        setComment('')
        setDocDate('')
        setLevel('')
        setError('')
      }
    }
  }, [isOpen, prefillPlayerId, editingId])

  const loadDiscountData = async (id: string) => {
    try {
      const data = await apiGet<any>(`/player-fee-corrections/${id}`)
      const mode: CorrectionMode = data.correctionMode === 'SURCHARGE' ? 'SURCHARGE' : 'DISCOUNT'
      setPlayerId(data.playerId || '')
      setAmount(data.amount?.toString() || '')
      setCorrectionMode(mode)
      setReason(
        data.reason ||
          (mode === 'SURCHARGE' ? 'OTHER' : 'LOYALTY')
      )
      setComment(data.comment || '')
      const dateValue = data.createdAt ? new Date(data.createdAt).toISOString().split('T')[0] : ''
      setDocDate(dateValue)
      setLevel(data.level || '')
      setError('')
    } catch (error: any) {
      console.error('Ошибка загрузки данных коррекции:', error)
      setError('Ошибка загрузки данных коррекции')
    }
  }

  const playersInTournament = players.filter(p => participantIds.has(p.id)).sort((a,b)=>a.name.localeCompare(b.name,'ru'))
  const playerLevels = useMemo(() => {
    const m = tournament.memberships || []
    const lvls = new Set<string>()
    for (const x of m) if (x.playerId === playerId && x.level) lvls.add(x.level)
    return Array.from(lvls)
  }, [tournament, playerId])

  useEffect(() => {
    if (correctionMode === 'SURCHARGE') {
      if (!['PENALTY', 'LATE_PAYMENT', 'DISCIPLINE', 'MATCH_RESCHEDULE_EXTRA_COURT_RESERVE', 'FIRST_MATCH_NO_SHOW_COURT_RESERVE', 'OTHER'].includes(reason)) setReason('OTHER')
      return
    }
    if (!['LOYALTY', 'MARKETING', 'ORGANIZER', 'SPONSOR', 'PRIZE', 'LOTTERY'].includes(reason)) setReason('LOYALTY')
  }, [correctionMode, reason])

  const submit = async () => {
    try {
      setSaving(true)
      setError('')
      if (!playerId || !amount) { setError('Укажите игрока и сумму'); setSaving(false); return }
      if (!level) { setError('Выберите уровень регистрации'); setSaving(false); return }
      if (!comment.trim()) { setError('Добавьте комментарий'); setSaving(false); return }
      const body: any = { playerId, tournamentId: tournament.id, amount: Number(amount), comment, reason, correctionMode, date: docDate || undefined, level }
      
      if (editingId) {
        await apiPut(`/player-fee-corrections/${editingId}`, body)
      } else {
        await apiPost('/player-fee-corrections', body)
      }
      
      onSuccess(); onClose()
    } catch (e:any) {
      setError(e?.response?.data?.error || e?.message || 'Ошибка сохранения')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-xl rounded shadow p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold">
            {editingId ? 'Редактировать коррекцию взноса' : 'Коррекция взноса'} — {tournament.name}
            {editingId && <div className="text-xs text-gray-500">ID: {editingId}</div>}
          </div>
          <button className="px-3 py-1 border rounded" onClick={onClose}>Закрыть</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Игрок</label>
            <select className="border rounded px-3 py-2 w-full" value={playerId} onChange={e=>setPlayerId(e.target.value)}>
              <option value="">— выберите игрока —</option>
              {playersInTournament.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Уровень</label>
            <select className="border rounded px-3 py-2 w-full" value={level} onChange={e=>setLevel(e.target.value)}>
              <option value="">— выберите уровень —</option>
              {playerLevels.map(l => (<option key={l} value={l}>{l}</option>))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Режим коррекции</label>
            <select className="border rounded px-3 py-2 w-full" value={correctionMode} onChange={e => setCorrectionMode(e.target.value as CorrectionMode)}>
              <option value="DISCOUNT">Скидка</option>
              <option value="SURCHARGE">Наценка</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Причина</label>
            <select className="border rounded px-3 py-2 w-full" value={reason} onChange={e=>setReason(e.target.value as any)}>
              {correctionMode === 'DISCOUNT' ? (
                <>
                  <option value="LOYALTY">Лояльность</option>
                  <option value="MARKETING">Маркетинг</option>
                  <option value="ORGANIZER">Организатор</option>
                  <option value="SPONSOR">Спонсор</option>
                  <option value="PRIZE">Призовые</option>
                  <option value="LOTTERY">Лотерея</option>
                </>
              ) : (
                <>
                  <option value="PENALTY">Штраф</option>
                  <option value="LATE_PAYMENT">Просрочка</option>
                  <option value="DISCIPLINE">Дисциплина</option>
                  <option value="MATCH_RESCHEDULE_EXTRA_COURT_RESERVE">Перенос матча и доп.резерв корта</option>
                  <option value="FIRST_MATCH_NO_SHOW_COURT_RESERVE">Неявка на первый матч и резерв корта</option>
                  <option value="OTHER">Другое</option>
                </>
              )}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Дата документа</label>
            <input type="date" className="border rounded px-3 py-2 w-full" value={docDate} onChange={e=>setDocDate(e.target.value)} />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs text-gray-600 mb-1">
              {correctionMode === 'DISCOUNT' ? 'Сумма уменьшения долга' : 'Сумма увеличения взноса'}
            </label>
            <input type="number" step="0.01" className="border rounded px-3 py-2 w-full" value={amount} onChange={e=>setAmount(e.target.value)} />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs text-gray-600 mb-1">Комментарий</label>
            <textarea className="border rounded px-3 py-2 w-full" rows={3} value={comment} onChange={e=>setComment(e.target.value)} />
          </div>
        </div>

        {error && <div className="text-red-600 text-sm mt-2">{error}</div>}

        <div className="mt-4 flex justify-end gap-2">
          <button className="px-3 py-2 border rounded" onClick={onClose}>Отмена</button>
          <button className="px-3 py-2 border rounded bg-white hover:bg-gray-50 disabled:opacity-50" onClick={submit} disabled={saving}>Сохранить</button>
        </div>
      </div>
    </div>
  )
}


